import { useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useWindowSize } from "@utils/hooks";
import { QtyRules } from "../types";
import { MobileSelect } from "@components/dropdowns";
import LoadingSpinner from "@components/loadingSpinner";
import { Button, Text } from "@components/theming";
import { Select } from "@components/inputs";
import { useIsShopsite } from "@context/DomainContext";

interface Props {
  qtyRules: QtyRules;
  handleDiscount: (
    discountId: number,
    isRetro: boolean,
    paQty: string
  ) => Promise<void>;
  isRetro: boolean;
  discountId: number;
  loading: boolean;
}

export default function DiscountDropdown({
  discountId,
  handleDiscount,
  isRetro,
  qtyRules,
  loading,
}: Props) {
  const isShopsite = useIsShopsite();
  const [selectedNum, setSelectedNum] = useState(qtyRules.Max);
  const windowWidth = useWindowSize().width;

  const t = useTranslations("Checkout.Cart.tickets.discount");

  return (
    <div className="flex flex-col gap-y-2 w-full">
      <div className="flex flex-1 items-center gap-x-6">
        <Text as="label" sharedClasses="block text-sm font-medium leading-6">
          {t("quantity")}
        </Text>
        <div>
          {windowWidth && windowWidth > 640 ? (
            <Select
              value={selectedNum.toString()}
              onValueChange={(value) => setSelectedNum(parseInt(value))}
              options={Array.from(
                { length: qtyRules.Max - qtyRules.Min + 1 },
                (_, index) => index + qtyRules.Min
              ).map((value) => ({
                label: value.toString(),
                value: value.toString(),
              }))}
              shopsiteButtonClasses="text-sm gap-2 px-4 focus:px-[15px] !rounded"
            />
          ) : (
            <MobileSelect
              options={Array.from(
                { length: qtyRules.Max - qtyRules.Min + 1 },
                (_, index) => index + qtyRules.Min
              ).map((v) => ({ label: v, value: v }))}
              onOptionSelect={(option) => setSelectedNum(parseInt(option))}
            >
              {(selected) => (
                <Button
                  variant="surface"
                  shopsiteVariant="white"
                  color="gray"
                  whitelabelClasses="bg-[color:var(--color-surface)] text-[color:var(--gray-12)]"
                  shopsiteClasses="text-sm !rounded h-9 px-4"
                >
                  <div className="inline-flex items-center justify-center gap-1">
                    {selected || qtyRules.Min}
                    <ChevronDownIcon className="w-4 h-4" />
                  </div>
                </Button>
              )}
            </MobileSelect>
          )}
        </div>
      </div>
      <Button
        type="button"
        highContrast
        disabled={loading}
        onClick={async () => {
          await handleDiscount(
            discountId,
            isRetro,
            `${qtyRules.Id}=${selectedNum}`
          );
        }}
        sharedClasses="w-full mt-2"
        shopsiteClasses="text-sm bg-indigo-600"
      >
        {loading ? (
          <LoadingSpinner
            size="20"
            color={isShopsite ? "#ffffff" : "var(--gray-11)"}
          />
        ) : (
          t("apply")
        )}
      </Button>
    </div>
  );
}
